import { api } from "@/api/client";
import { toast } from "sonner";

/** "Preview as Client" from a PROJECT page (2026-10 client-portal preview fix) — mirrors
 * lib/agencyPortalPreview.js's openAgencyPortalPreview() exactly, and deliberately NOT
 * openClientPortalPreview above: that one is COMPANY/person-based (the server auto-picks a real
 * Contact, which a project with no contact — or a client never pilot-flagged — simply doesn't
 * have), which is why it dead-ended staff on the client sign-in page reading "We couldn't find
 * an active project for that email". This one hands the server a PROJECT id: no client login,
 * no email lookup, no portal-access flags, works for closed/delivered projects and for projects
 * with no client contact at all. Read-only (writes 403 `preview_is_read_only`) and
 * non-destructive to the staff session (its own `njm_client_project_preview_access` cookie).
 * Safari/Firefox only honor `window.open` synchronously inside the click handler — open a blank
 * tab FIRST, before any await, then redirect it once the mint call resolves. */
export async function openClientProjectPreview(projectId, projectName) {
  if (!window.confirm(`Open the real Client Portal for "${projectName}"?\n\nRead-only preview — opens in a new tab, your own staff session stays signed in.`)) {
    return false;
  }
  const tab = window.open("", "_blank");
  if (!tab) {
    toast.error("Your browser blocked the new tab — allow pop-ups for this site and try again.");
    return false;
  }
  try {
    const r = await api.post(`/v1/client-portal/admin/projects/${projectId}/preview`);
    tab.location.href = `${window.location.origin}/api/v1/client-portal/auth/verify-project-preview?token=${r.data.token}`;
    toast.success(`Opening ${r.data.project_name || projectName} as the client — read-only preview`);
    return true;
  } catch (e) {
    tab.close();
    toast.error(e?.response?.data?.detail?.code === "project_not_found" ? "This project no longer exists." : "Couldn't open the preview — try again.");
    return false;
  }
}

/** Opens the real Client Portal, as a real CONTACT of a client company, in a new tab — for
 * staff to check what that client sees. Read-only and non-destructive to the caller's own staff
 * session (2026-09 PM Client Portal Preview fix — see
 * /app/memory/NJM_OS_PM_CLIENT_PORTAL_PREVIEW_AUDIT.md): a dedicated `client_preview_link`
 * token + `njm_preview_access` cookie (never `njm_access`), so this can no longer sign the
 * staff tab out, and every client-side write is server-rejected for this session regardless of
 * what the UI shows. Attributed server-side to whichever staff member clicked it. Shared by
 * ClientRow's overflow menu, ClientDetail's top action bar, and Admin.jsx's "Client Portal
 * Access" table — all three must call this SAME function, not their own copy, so a future fix
 * here never has to be repeated three times. For a PROJECT page, use
 * openClientProjectPreview above instead — it needs no contact and no portal access flag.
 * Safari/Firefox only honor `window.open` synchronously inside the click handler — open a
 * blank tab FIRST, before any await, then redirect it once the mint call resolves. */
export async function openClientPortalPreview(companyRemoteId, companyName) {
  if (!window.confirm(`Open the real Client Portal as "${companyName}"?\n\nRead-only preview — opens in a new tab, your own staff session stays signed in.`)) {
    return false;
  }
  const tab = window.open("", "_blank");
  if (!tab) {
    toast.error("Your browser blocked the new tab — allow pop-ups for this site and try again.");
    return false;
  }
  try {
    const r = await api.post(`/v1/client-portal/admin/portal-access/${companyRemoteId}/preview`);
    tab.location.href = `${window.location.origin}/api/v1/client-portal/auth/verify-preview?token=${r.data.token}`;
    toast.success(`Opening as ${r.data.contact_name} (${r.data.contact_email}) — read-only preview`);
    return true;
  } catch (e) {
    tab.close();
    const code = e?.response?.data?.detail?.code;
    // 2026-06: `company_not_enabled` used to fall through to the generic retry message (or,
// if the entry point didn't disable its button off a stale `portal_enabled`, to the client
    // sign-in error page) — neither told staff the one thing they had to do.
    toast.error(
      code === "no_contact_for_company" ? "No active contact found on this client — nothing to preview as."
        : code === "company_not_enabled" ? `Client Portal access isn't enabled for ${companyName} yet — turn it on under "Manage Client Portal" on the client's page first.`
          : "Couldn't open the preview — try again.",
    );
    return false;
  }
}
